import { ArrowListExpand, IcoRemove } from "@/utils/icons";
import classes from "./index.module.css";
import { useEffect, useState } from "react";
import { departments as departmentsApi } from "@/api";
import { Checkbox } from "antd";
import classNames from "classnames";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import Preloader from "@UI/Preloader";

interface IDepartment {
  id: number;
  name: string;
  parent_id: number | null;
  head_id: number | null;
  order: number;
  children: IDepartment[];
}

const checkIsSelected = (
  department: IDepartment,
  selected: IDepartment | null
): boolean => {
  if (!selected || department.id === selected.id) return false;

  return checkIsNestedSelected(department, selected);
};

const checkIsNestedSelected = (
  department: IDepartment,
  selected: IDepartment
): boolean => {
  if (department.id === selected.id) {
    return true;
  }

  return department.children.some((child) =>
    checkIsNestedSelected(child, selected)
  );
};

const DepartmentView = ({
  department,
  selectedDepartment,
  selectDepartment,
}: {
  department: IDepartment;
  selectedDepartment: IDepartment | null;
  selectDepartment: (department: IDepartment | null) => void;
}) => {
  const isTopLevelItem = department?.parent_id === null;
  const isSelected = department.id === selectedDepartment?.id;
  const [isOpenDownLevel, setIsOpenDownLevel] = useState(
    checkIsSelected(department, selectedDepartment)
  );

  const openDepartment = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpenDownLevel((prev) => !prev);
  };

  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    selectDepartment(
      selectedDepartment?.id === department.id ? null : department
    );
  };

  return (
    <div
      className={classes.departmentList}
      style={{ marginLeft: isTopLevelItem ? "0px" : "40px" }}
    >
      <div
        className={classes.departmentItem}
        style={{
          backgroundColor: isSelected
            ? "#E2E8F0"
            : isTopLevelItem
            ? "white"
            : "#F4F4F4",
        }}
      >
        <div className={classes.departmentItemInfoWrapper}>
          <Checkbox checked={isSelected} onChange={handleCheckboxChange} />
          <div
            className={classNames(
              classes.departmentTitle,
              classes.truncateText
            )}
          >
            {department.name}
          </div>
        </div>
        {department.children?.length > 0 && (
          <div
            className={classes.arrowListWrapper}
            style={{
              transform: isOpenDownLevel ? "rotate(0deg)" : "rotate(90deg)",
            }}
            onClick={openDepartment}
          >
            <ArrowListExpand />
          </div>
        )}
      </div>
      {department.children?.length > 0 && isOpenDownLevel && (
        <div className={classes.departmentList}>
          {department.children
            .sort((a, b) => a.order - b.order)
            .map((child) => (
              <DepartmentView
                key={child.id}
                department={child}
                selectedDepartment={selectedDepartment}
                selectDepartment={selectDepartment}
              />
            ))}
        </div>
      )}
    </div>
  );
};

const DepartmentTree = ({
  departments,
  selectedDepartment,
  selectDepartment,
}: {
  departments: IDepartment[];
  selectedDepartment: IDepartment | null;
  selectDepartment: (department: IDepartment | null) => void;
}) => {
  return (
    <div className={classes.departmentList}>
      {departments
        .sort((a, b) => a.order - b.order)
        .map((department) => (
          <DepartmentView
            key={department.id}
            department={department}
            selectedDepartment={selectedDepartment}
            selectDepartment={selectDepartment}
          />
        ))}
    </div>
  );
};

const SelectDepartmentModal = ({
  department = undefined,
  title = "Выбор отдела",
  onSelect = (department: IDepartment | null) => {},
  onDismiss = () => {},
}: {
  department?: IDepartment;
  title: string;
  onSelect: (department: IDepartment | null) => void;
  onDismiss: () => void;
}) => {
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [departments, setDepartments] = useState<IDepartment[]>([]);
  const [selectedDepartment, setSelectedDepartment] =
    useState<IDepartment | null>(department ?? null);

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = () => {
    setLoadingDepartments(true);
    departmentsApi
      .getAll()
      .then((res) => {
        setDepartments(res.data);
      })
      .finally(() => {
        setLoadingDepartments(false);
      });
  };

  const selectDepartment = (department: IDepartment | null) => {
    setSelectedDepartment(department);
  };

  const handleConfirm = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    onSelect(selectedDepartment);
  };

  return (
    <div className={classes.modalContainer}>
      <div className={classes.modalHeader}>
        <div className={classes.modalTitleContainer}>
          <span className={classes.modalHeaderTitle}>{title}</span>
        </div>
        <div className={classes.closeIcon} onClick={onDismiss}>
          <IcoRemove />
        </div>
      </div>
      <div className={classes.formWrapper}>
        <DepartmentTree
          departments={departments}
          selectedDepartment={selectedDepartment}
          selectDepartment={selectDepartment}
        />
        {loadingDepartments && (
          <div style={{ alignSelf: "center" }}>
            <Preloader />
          </div>
        )}
      </div>
      <div className={classes.actions}>
        <button className={classes.submitButton} onClick={handleConfirm}>
          Подтвердить
        </button>
      </div>
    </div>
  );
};

export default SelectDepartmentModal;
